import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { UnifiedLoginModal } from '../pages/authentication/components/UnifiedLoginModal';
import { Box, Heading, Text, VStack, Button, Badge, Input, Tooltip, useColorMode, useToast } from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';
import { useLazyQuery, useMutation } from '@apollo/client';
import { SEARCH_CLIENTS, ADD_CLIENT_PERMISSION, REMOVE_CLIENT_PERMISSION } from '../pages/clients/queries';
import { Client, ClientPermission } from '../generated/graphql';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredPermissions?: string[];
}

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  requiredPermissions = []
}) => {
  const { isAuthenticated, user, loading, refreshAuth } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { colorMode } = useColorMode();
  const toast = useToast();
  const isDev = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
  const devBadgeHiddenByDefault = process.env.REACT_APP_HIDE_DEV_BADGE === 'true';

  // Dev badge visibility (persisted in localStorage, env var sets default only)
  const [showDevBadge, setShowDevBadge] = useState(() => {
    if (!isDev) return false;
    const stored = localStorage.getItem('devBadgeVisible');
    if (stored !== null) return stored === 'true';
    return !devBadgeHiddenByDefault;
  });

  const toggleDevBadge = (visible: boolean) => {
    setShowDevBadge(visible);
    localStorage.setItem('devBadgeVisible', String(visible));
  };

  // Press 'D' to toggle the dev badge
  useEffect(() => {
    if (!isDev) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'd' || e.key === 'D') {
        const tag = (e.target as HTMLElement).tagName;
        if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || (e.target as HTMLElement).isContentEditable) return;
        toggleDevBadge(true);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isDev]);

  // Dev badge state
  const [activePermission, setActivePermission] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [badgePos, setBadgePos] = useState({ x: window.innerWidth - 340, y: window.innerHeight - 120 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [selectedClient, setSelectedClient] = useState<Pick<Client, 'id' | 'fName' | 'lName' | 'permissions'> | null>(null);
  const [showMyPerms, setShowMyPerms] = useState(false);
  const [compareSearch, setCompareSearch] = useState('');

  const [searchClients, { data: searchData, loading: searchLoading }] = useLazyQuery(SEARCH_CLIENTS);
  const [compareSearchClients, { data: compareData, loading: compareLoading }] = useLazyQuery(SEARCH_CLIENTS);
  const [addPermission] = useMutation(ADD_CLIENT_PERMISSION);
  const [removePermission] = useMutation(REMOVE_CLIENT_PERMISSION);

  // Drag handlers for dev badge
  const handleDragStart = (e: React.MouseEvent) => {
    if ((e.target as HTMLElement).closest('.dev-badge-drag-handle')) {
      setIsDragging(true);
      setDragStart({ x: e.clientX - badgePos.x, y: e.clientY - badgePos.y });
    }
  };

  useEffect(() => {
    if (!isDragging) return;
    const onMove = (e: MouseEvent) => {
      setBadgePos({ x: e.clientX - dragStart.x, y: e.clientY - dragStart.y });
    };
    const onUp = () => setIsDragging(false);
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
    return () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
    };
  }, [isDragging, dragStart]);

  const handleBadgeClick = (permission: string) => {
    if (activePermission === permission) {
      setActivePermission(null);
      setSearchTerm('');
    } else {
      setActivePermission(permission);
      setSearchTerm('');
    }
  };

  const handleSearch = (value: string) => {
    setSearchTerm(value);
    if (value.length >= 2) {
      searchClients({ variables: { search: value } });
    }
  };

  const handleGrantPermission = async (clientId: string, clientName: string, permission?: string) => {
    const perm = permission || activePermission;
    try {
      await addPermission({
        variables: { clientId, permission: perm }
      });
      toast({
        title: `Added ${perm} to ${clientName}`,
        status: 'success',
        duration: 3000,
      });
      if (selectedClient && selectedClient.id === clientId) {
        setSelectedClient({
          ...selectedClient,
          permissions: [...(selectedClient.permissions || []), perm as ClientPermission]
        });
      }
      if (!permission) {
        setActivePermission(null);
        setSearchTerm('');
      }
      if (clientId === user?.id) refreshAuth();
    } catch (err: unknown) {
      toast({
        title: 'Failed to add permission',
        description: err instanceof Error ? err.message : 'Unknown error',
        status: 'error',
        duration: 5000,
      });
    }
  };

  const handleRemovePermission = async (clientId: string, clientName: string, permission: string) => {
    try {
      await removePermission({ variables: { clientId, permission } });
      toast({
        title: `Removed ${permission} from ${clientName}`,
        status: 'warning',
        duration: 3000,
      });
      if (selectedClient && selectedClient.id === clientId) {
        setSelectedClient({
          ...selectedClient,
          permissions: (selectedClient.permissions || []).filter(p => p !== permission)
        });
      }
      if (clientId === user?.id) refreshAuth();
    } catch (err: unknown) {
      toast({
        title: 'Failed to remove permission',
        description: err instanceof Error ? err.message : 'Unknown error',
        status: 'error',
        duration: 5000,
      });
    }
  };

  const handleCompareSearch = (value: string) => {
    setCompareSearch(value);
    if (value.length >= 2) {
      compareSearchClients({ variables: { search: value } });
    }
  };

  const handleSelectClient = (client: Pick<Client, 'id' | 'fName' | 'lName' | 'permissions'>) => {
    setSelectedClient(client);
    setCompareSearch('');
  };

  // Wait for auth to load
  if (loading) {
    return (
      <Box minH="100vh" display="flex" alignItems="center" justifyContent="center">
        <Text>Loading...</Text>
      </Box>
    );
  }

  // Seamless page auth: show login modal instead of redirecting to home
  if (!isAuthenticated) {
    return (
      <>
        <Box minH="100vh" />
        <UnifiedLoginModal
          returnPath={window.location.pathname + window.location.search + window.location.hash}
          isOpen={true}
          onClose={() => {}}
          onSuccess={(token: string) => {
            localStorage.setItem('auth_token', token);
            refreshAuth();
          }}
        />
      </>
    );
  }

  // Check if user has required permissions
  if (requiredPermissions.length > 0) {
    const hasPermission = requiredPermissions.some(permission =>
      user?.permissions?.includes(permission)
    );

    if (!hasPermission) {
      return (
        <Box minH="100vh" display="flex" alignItems="center" justifyContent="center" bg="gray.50">
          <VStack spacing={4} textAlign="center" p={8}>
            <Heading size="lg" color="red.500">Access Denied</Heading>
            <Text color="gray.600">
              You don't have permission to access this page.
            </Text>
            <Text fontSize="sm" color="gray.500">
              Required permissions: {requiredPermissions.join(', ')}
            </Text>
            <Button
              colorScheme="blue"
              onClick={() => navigate('/')}
              mt={4}
            >
              Go to Home
            </Button>
          </VStack>
        </Box>
      );
    }
  }

  const clients = searchData?.searchClients || [];
  const compareClients = compareData?.searchClients || [];
  const myPerms: string[] = user?.permissions || [];

  return (
    <>
      {children}
      {isDev && showDevBadge && (
        <Box
          position="fixed"
          left={`${badgePos.x}px`}
          top={`${badgePos.y}px`}
          bg={colorMode === 'light' ? 'gray.800' : 'gray.700'}
          color="white"
          p={3}
          borderRadius="md"
          fontSize="xs"
          opacity={0.8}
          maxW="360px"
          maxH="80vh"
          overflowY="auto"
          zIndex={9999}
          boxShadow="lg"
          _hover={{ opacity: 1 }}
          onMouseDown={handleDragStart}
          userSelect={isDragging ? 'none' : 'auto'}
        >
          <VStack align="start" spacing={2}>
            <Box display="flex" justifyContent="space-between" alignItems="center" w="100%">
              <Text
                className="dev-badge-drag-handle"
                fontWeight="bold"
                fontSize="xs"
                color="yellow.300"
                cursor="grab"
              >
                ⠿ DEV: {location.pathname}
              </Text>
              <Text
                fontSize="xs"
                color="gray.400"
                cursor="pointer"
                onClick={() => toggleDevBadge(false)}
                _hover={{ color: 'red.300' }}
                title="Hide dev badge (press D to bring back)"
                ml={2}
              >
                ✕
              </Text>
            </Box>

            {/* Route required permissions */}
            {requiredPermissions.length > 0 && (
              <>
                <Text fontSize="xs" color="gray.300">Required permissions:</Text>
                <Box display="flex" flexWrap="wrap" gap={1}>
                  {requiredPermissions.map((p) => {
                    const hasIt = myPerms.includes(p);
                    return (
                      <Badge
                        key={p}
                        colorScheme={hasIt ? 'green' : 'red'}
                        fontSize="8px"
                        variant="subtle"
                        cursor="pointer"
                        onClick={() => handleBadgeClick(p)}
                        _hover={{ opacity: 0.7 }}
                        outline={activePermission === p ? '2px solid white' : 'none'}
                      >
                        {hasIt ? '✓' : '✗'} {p}
                      </Badge>
                    );
                  })}
                </Box>
              </>
            )}

            {/* Grant permission search (existing flow) */}
            {activePermission && (
              <Box
                p={2}
                bg="gray.900"
                borderRadius="md"
                w="100%"
                border="1px solid"
                borderColor="yellow.300"
              >
                <Text fontSize="xs" color="yellow.300" mb={1}>
                  Grant <strong>{activePermission}</strong> to:
                </Text>
                <Input
                  size="xs"
                  placeholder="Search client by name..."
                  value={searchTerm}
                  onChange={(e) => handleSearch(e.target.value)}
                  bg="gray.700"
                  border="none"
                  color="white"
                  mb={1}
                  autoFocus
                />
                {searchLoading && (
                  <Text fontSize="xs" color="gray.400">Searching...</Text>
                )}
                {searchTerm.length >= 2 && clients.length > 0 && (
                  <VStack align="stretch" spacing={0} maxH="120px" overflowY="auto">
                    {clients.slice(0, 5).map((c: Pick<Client, 'id' | 'fName' | 'lName' | 'email' | 'permissions'>) => {
                      const perms = c.permissions || [];
                      const hasTarget = activePermission ? perms.includes(activePermission as ClientPermission) : false;
                      return (
                        <Tooltip
                          key={c.id}
                          label={perms.length > 0 ? `Permissions (${perms.length}):\n${perms.join(', ')}` : 'No permissions'}
                          placement="left"
                          fontSize="8px"
                          maxW="250px"
                          whiteSpace="pre-wrap"
                          hasArrow
                        >
                          <Box
                            px={2}
                            py={1}
                            cursor="pointer"
                            borderRadius="sm"
                            _hover={{ bg: 'gray.600' }}
                            onClick={() => handleGrantPermission(c.id, `${c.fName} ${c.lName}`)}
                          >
                            <Text fontSize="xs" color="white">
                              {c.fName} {c.lName}
                              {hasTarget && (
                                <Badge ml={1} colorScheme="yellow" fontSize="7px" variant="subtle">
                                  already has
                                </Badge>
                              )}
                            </Text>
                            <Text fontSize="8px" color="gray.400">{c.email}</Text>
                          </Box>
                        </Tooltip>
                      );
                    })}
                  </VStack>
                )}
                {searchTerm.length >= 2 && !searchLoading && clients.length === 0 && (
                  <Text fontSize="xs" color="gray.400">No clients found</Text>
                )}
                <Text
                  fontSize="8px"
                  color="gray.500"
                  mt={1}
                  cursor="pointer"
                  onClick={() => { setActivePermission(null); setSearchTerm(''); }}
                  _hover={{ color: 'gray.300' }}
                >
                  [cancel]
                </Text>
              </Box>
            )}

            {/* My Permissions toggle */}
            <Text
              fontSize="xs"
              color="cyan.300"
              cursor="pointer"
              onClick={() => setShowMyPerms(!showMyPerms)}
              _hover={{ color: 'cyan.200' }}
            >
              {showMyPerms ? '▼' : '▶'} My Perms ({myPerms.length})
            </Text>
            {showMyPerms && (
              <Box p={2} bg="gray.900" borderRadius="md" w="100%" border="1px solid" borderColor="cyan.600">
                <Box display="flex" flexWrap="wrap" gap={1} maxH="150px" overflowY="auto">
                  {myPerms.map((p) => {
                    const isRequired = requiredPermissions.includes(p);
                    return (
                      <Badge
                        key={p}
                        colorScheme={isRequired ? 'green' : 'gray'}
                        fontSize="7px"
                        variant="subtle"
                        cursor="pointer"
                        onClick={() => handleRemovePermission(user?.id || '', 'Me', p)}
                        _hover={{ opacity: 0.7 }}
                        title={`Click to remove ${p} from yourself`}
                      >
                        {p} ✗
                      </Badge>
                    );
                  })}
                </Box>
                {requiredPermissions.filter(p => !myPerms.includes(p)).length > 0 && (
                  <Box mt={2}>
                    <Text fontSize="8px" color="red.300">Missing for this route:</Text>
                    <Box display="flex" flexWrap="wrap" gap={1} mt={1}>
                      {requiredPermissions.filter(p => !myPerms.includes(p)).map((p) => (
                        <Badge
                          key={p}
                          colorScheme="red"
                          fontSize="7px"
                          variant="subtle"
                          cursor="pointer"
                          onClick={() => handleGrantPermission(user?.id || '', 'Me', p)}
                          _hover={{ opacity: 0.7 }}
                          title={`Click to add ${p} to yourself`}
                        >
                          + {p}
                        </Badge>
                      ))}
                    </Box>
                  </Box>
                )}
              </Box>
            )}

            {/* Compare Client section */}
            <Box w="100%" borderTop="1px solid" borderColor="gray.600" pt={2}>
              <Text fontSize="xs" color="orange.300" mb={1}>Compare Client:</Text>
              {selectedClient ? (
                <Box p={2} bg="gray.900" borderRadius="md" border="1px solid" borderColor="orange.600">
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                    <Text fontSize="xs" color="orange.300" fontWeight="bold">
                      {selectedClient.fName} {selectedClient.lName}
                    </Text>
                    <Text
                      fontSize="8px"
                      color="gray.500"
                      cursor="pointer"
                      onClick={() => setSelectedClient(null)}
                      _hover={{ color: 'gray.300' }}
                    >
                      [clear]
                    </Text>
                  </Box>
                  <Text fontSize="8px" color="gray.400" mb={1}>
                    Permissions ({(selectedClient.permissions || []).length}):
                  </Text>
                  <Box display="flex" flexWrap="wrap" gap={1} maxH="150px" overflowY="auto">
                    {(selectedClient.permissions || []).map((p) => {
                      const isRequired = requiredPermissions.includes(p as string);
                      return (
                        <Badge
                          key={p}
                          colorScheme={isRequired ? 'green' : 'blue'}
                          fontSize="7px"
                          variant="subtle"
                          cursor="pointer"
                          onClick={() => handleRemovePermission(selectedClient.id, `${selectedClient.fName}`, p as string)}
                          _hover={{ opacity: 0.7 }}
                          title={`Click to remove ${p} from ${selectedClient.fName}`}
                        >
                          {p} ✗
                        </Badge>
                      );
                    })}
                  </Box>
                  {/* Missing permissions for this route */}
                  {requiredPermissions.filter(p => !(selectedClient.permissions || []).includes(p as ClientPermission)).length > 0 && (
                    <Box mt={2}>
                      <Text fontSize="8px" color="red.300">Missing for this route:</Text>
                      <Box display="flex" flexWrap="wrap" gap={1} mt={1}>
                        {requiredPermissions.filter(p => !(selectedClient.permissions || []).includes(p as ClientPermission)).map((p) => (
                          <Badge
                            key={p}
                            colorScheme="red"
                            fontSize="7px"
                            variant="subtle"
                            cursor="pointer"
                            onClick={() => handleGrantPermission(selectedClient.id, `${selectedClient.fName}`, p)}
                            _hover={{ opacity: 0.7 }}
                            title={`Click to add ${p} to ${selectedClient.fName}`}
                          >
                            + {p}
                          </Badge>
                        ))}
                      </Box>
                    </Box>
                  )}
                </Box>
              ) : (
                <Box>
                  <Input
                    size="xs"
                    placeholder="Search client to compare..."
                    value={compareSearch}
                    onChange={(e) => handleCompareSearch(e.target.value)}
                    bg="gray.700"
                    border="none"
                    color="white"
                    mb={1}
                  />
                  {compareLoading && (
                    <Text fontSize="xs" color="gray.400">Searching...</Text>
                  )}
                  {compareSearch.length >= 2 && compareClients.length > 0 && (
                    <VStack align="stretch" spacing={0} maxH="120px" overflowY="auto">
                      {compareClients.slice(0, 5).map((c: Pick<Client, 'id' | 'fName' | 'lName' | 'email' | 'permissions'>) => (
                        <Box
                          key={c.id}
                          px={2}
                          py={1}
                          cursor="pointer"
                          borderRadius="sm"
                          _hover={{ bg: 'gray.600' }}
                          onClick={() => handleSelectClient(c)}
                        >
                          <Text fontSize="xs" color="white">
                            {c.fName} {c.lName}
                            <Badge ml={1} colorScheme="blue" fontSize="7px" variant="subtle">
                              {(c.permissions || []).length} perms
                            </Badge>
                          </Text>
                          <Text fontSize="8px" color="gray.400">{c.email}</Text>
                        </Box>
                      ))}
                    </VStack>
                  )}
                  {compareSearch.length >= 2 && !compareLoading && compareClients.length === 0 && (
                    <Text fontSize="xs" color="gray.400">No clients found</Text>
                  )}
                </Box>
              )}
            </Box>
          </VStack>
        </Box>
      )}
    </>
  );
};
